import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { FaTimes, FaUpload, FaTrash, FaImage } from 'react-icons/fa';
import { assetUrl } from '../../utils/img';
import './ImageUploader.scss';
function ImageUploader({ isOpen, onClose, onUpload, onRemove, currentImage, restorable, onRestore, title = "Upload Image", acceptedTypes = "image/*", maxSizeInMB = 5 }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [imageDimensions, setImageDimensions] = useState(null);
    const [currentImageDimensions, setCurrentImageDimensions] = useState(null);
    const fileInputRef = useRef(null);
    // Get dimensions for current image when it loads
    useEffect(() => {
        var _a;
        if (currentImage && !selectedFile) {
            const img = new Image();
            img.onload = () => {
                setCurrentImageDimensions({ width: img.width, height: img.height });
            };
            img.src = (_a = assetUrl(currentImage.imageUrl)) !== null && _a !== void 0 ? _a : currentImage.imageUrl;
        }
    }, [currentImage, selectedFile]);
    if (!isOpen)
        return null;
    const validateFile = (file) => {
        if (!file.type.startsWith('image/')) {
            return 'Please select an image file';
        }
        if (file.size > maxSizeInMB * 1024 * 1024) {
            return `File size must be less than ${maxSizeInMB}MB`;
        }
        return null;
    };
    const handleFile = (file) => {
        const error = validateFile(file);
        if (error) {
            alert(error);
            return;
        }
        setSelectedFile(file);
        // Clean up previous preview URL
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        const newPreviewUrl = URL.createObjectURL(file);
        setPreviewUrl(newPreviewUrl);
        // Get image dimensions
        const img = new Image();
        img.onload = () => {
            setImageDimensions({ width: img.width, height: img.height });
        };
        img.src = newPreviewUrl;
    };
    const handleFileSelect = (event) => {
        var _a;
        const file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (file) {
            handleFile(file);
        }
    };
    const handleUpload = () => {
        if (selectedFile) {
            onUpload(selectedFile);
            handleClose();
        }
    };
    const handleRemove = () => {
        if (onRemove) {
            onRemove();
            handleClose();
        }
    };
    const handleClose = () => {
        // Clean up preview URL
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setSelectedFile(null);
        setImageDimensions(null);
        setCurrentImageDimensions(null);
        onClose();
    };
    const openFileDialog = () => {
        var _a;
        (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    };
    // Determine which image to show and its dimensions
    const displayImage = selectedFile && previewUrl ? {
        imageUrl: previewUrl,
        filename: selectedFile.name,
        uploadedAt: new Date().toISOString(),
        dimensions: imageDimensions
    } : currentImage ? Object.assign(Object.assign({}, currentImage), { dimensions: currentImageDimensions }) : null;
    return (_jsx("div", { className: "image-uploader-overlay", onClick: handleClose, children: _jsxs("div", { className: "image-uploader-modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "modal-header", children: [_jsx("h3", { children: title }), _jsx("button", { className: "close-button", onClick: handleClose, children: _jsx(FaTimes, {}) })] }), _jsxs("div", { className: "modal-body", children: [displayImage && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "current-image-wrapper", children: [_jsx("img", { src: assetUrl(displayImage.imageUrl), alt: "Current", className: "current-image" }), onRemove && !selectedFile && (_jsx("button", { className: "remove-icon", onClick: handleRemove, title: "Remove Image", children: _jsx(FaTrash, {}) }))] }), _jsxs("div", { className: "image-info", children: [_jsxs("div", { className: "filename-container", children: [_jsx("span", { className: "filename", children: displayImage.filename }), displayImage.dimensions && (_jsxs("span", { className: "dimensions", children: [displayImage.dimensions.width, "\u00D7", displayImage.dimensions.height, "px"] }))] }), _jsx("span", { className: "upload-date", children: new Date(displayImage.uploadedAt).toLocaleDateString() })] })] })), !displayImage && (_jsxs("div", { className: "upload-area", onClick: openFileDialog, children: [_jsx(FaImage, { className: "upload-icon" }), _jsx("p", { className: "upload-text", children: "Click to select image" }), _jsxs("p", { className: "format-info", children: ["Supports: JPG, PNG, GIF, WebP (max ", maxSizeInMB, "MB)"] })] })), _jsx("input", { ref: fileInputRef, type: "file", accept: acceptedTypes, onChange: handleFileSelect, style: { display: 'none' } })] }), _jsxs("div", { className: "modal-actions", children: [selectedFile && (_jsxs("button", { className: "upload-button primary", onClick: handleUpload, children: [_jsx(FaUpload, {}), " Save"] })), restorable && onRestore && !selectedFile && (_jsx("button", { className: "restore-button primary", onClick: () => { onRestore(); }, children: "Restore Image" })), currentImage && !selectedFile && (_jsx("button", { className: "replace-button primary", onClick: openFileDialog, children: "Replace Image" })), _jsx("button", { className: "cancel-button secondary", onClick: handleClose, children: "Cancel" })] })] }) }));
}
export default ImageUploader;
